import { FC, useMemo } from 'react';
import { Event, EventRegistrationForm, EventRegistrationFormStatus } from '../generated/api/dsv-public/model';
import { Stack, Table, TableBody, TableCell, TableContainer, TableRow, Typography } from '@mui/material';
import { prettyPrintDate, prettyPrintDateRange, prettyPrintTimeRange } from '../util/helper';
import { useTranslation } from 'react-i18next';

type EventInformationProps = {
  event?: Event;
  registrationForm?: EventRegistrationForm;
};

export const EventInformation: FC<EventInformationProps> = ({ event, registrationForm }) => {
  const { t } = useTranslation();

  const remainingSeats = useMemo(() => {
    if (registrationForm?.status === EventRegistrationFormStatus.WAITING_LIST) {
      const seats = registrationForm.remainingWaitingListSeats ?? '';
      return registrationForm.maxWaitingListSeats
        ? `${t('forms.event-info.waiting-list')}: ${seats} (von ${registrationForm.maxWaitingListSeats})`
        : `${t('forms.event-info.waiting-list')}: ${seats}`;
    }
    const seats = registrationForm?.remainingSeats ?? '';
    return registrationForm?.maxParticipants ? `${seats} (von ${registrationForm.maxParticipants})` : seats;
  }, [t, registrationForm]);

  const EventInformationRow: FC<{ label: string; value: string }> = ({ label, value }) => {
    return (
      <TableRow>
        <TableCell sx={{ borderBottom: 'none', padding: '5px 10px' }}>
          <Typography fontWeight={600}>{label}</Typography>
        </TableCell>
        <TableCell sx={{ borderBottom: 'none', padding: '5px 10px' }}>
          <Typography>{value}</Typography>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Stack margin="auto">
      <h2>{t('forms.event-info.title')}</h2>
      <TableContainer>
        <Table sx={{ backgroundColor: 'rgba(0, 0, 0, 0)', width: 'unset', margin: 'auto' }}>
          <TableBody>
            {event?.name && <EventInformationRow label={t('forms.event-info.event')} value={event.name} />}
            {event?.startDate && (
              <EventInformationRow
                label={t('forms.event-info.date')}
                value={prettyPrintDateRange(event?.startDate, event?.endDate)}
              />
            )}
            {event?.startTime && (
              <EventInformationRow
                label={t('forms.event-info.time')}
                value={prettyPrintTimeRange(event?.startTime, event?.endTime)}
              />
            )}
            {event?.organiser && (
              <EventInformationRow label={t('forms.event-info.organizer')} value={event.organiser} />
            )}
            {registrationForm?.registrationEnd && (
              <EventInformationRow
                label={t('forms.event-info.registration-deadline')}
                value={prettyPrintDate(registrationForm?.registrationEnd)}
              />
            )}
            {remainingSeats.length > 0 && (
              <EventInformationRow label={t('forms.event-info.available-seats')} value={remainingSeats} />
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};
